import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import type { Informe } from "@/lib/informes";

/**
 * Un documento que el presupuesto puede pedir: el tipo declarado por el informe.
 */
export type DocumentoElegible = { id: number; nombre: string };

/**
 * Los antecedentes LEGALES que el informe declara, que son los que el
 * presupuesto pide.
 *
 * `informe.documentos` LLEGA RESUELTO del servidor: incluye todo el catálogo
 * legal activo, con Obligatorio en lo que nadie eligió
 * (`Informe::documentosResueltos()`). Por eso acá basta con quitar "No aplica":
 * no hay que cruzar contra el catálogo ni saber que la ausencia significa
 * Obligatorio.
 *
 * Los HISTÓRICOS quedan fuera a propósito: son antecedentes internos que el
 * equipo consigue, no algo que se le pida al cliente en la carta.
 */
export function documentosDelInforme(informe: Informe): DocumentoElegible[] {
  return (informe.documentos ?? [])
    .filter((documento) => documento.categoria === "legal")
    // "No aplica" es la decisión de que el documento no corresponde a esta
    // propiedad: no se le pide al cliente en la carta.
    .filter((documento) => documento.obligatoriedad !== "no_aplica")
    .map((documento) => ({
      // El id del TIPO y no el de la fila del informe: es lo que la cotización
      // guarda, porque lo que se pide es el documento, no su declaración.
      id: documento.documento_tipo_id,
      nombre: documento.nombre ?? `Documento ${documento.documento_tipo_id}`,
    }));
}

/**
 * Elige qué documentación le pide el presupuesto al cliente.
 *
 * LA METODOLOGÍA YA NO SE ELIGE ACÁ (01-09-2026), y por eso este componente
 * quedó con un solo apartado. El selector de Metodología era el otro bloque: se
 * retiró de la pantalla y el documento imprime siempre la estándar, que arma el
 * servidor — ver `CotizacionItem::METODOLOGIA_ESTANDAR`. Es provisorio: la
 * elección vuelve cuando se defina el catálogo por tipo de activo, y mientras
 * tanto los ítems siguen enteros en la base y en su mantenedor.
 *
 * LA DOCUMENTACIÓN SALE DEL INFORME, no de un catálogo propio: es la misma
 * lista que se marcó como Documentación Legal, y tener dos obligaba a
 * mantenerlas parecidas a mano. Nace TODA MARCADA porque el caso normal es
 * pedir lo mismo que el expediente necesita; desmarcar algo es la excepción, y
 * por eso es una decisión y no un trabajo de arranque.
 *
 * Componente CONTROLADO: el formulario es dueño de la selección.
 */
export function SelectorDeItems({
  documentos,
  documentosElegidos,
  onDocumentosChange,
}: {
  documentos: DocumentoElegible[];
  documentosElegidos: number[];
  onDocumentosChange: (elegidos: number[]) => void;
}) {
  function alternar(id: number) {
    onDocumentosChange(
      documentosElegidos.includes(id)
        ? documentosElegidos.filter((elegido) => elegido !== id)
        : [...documentosElegidos, id],
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Label>Documentación</Label>
        <p className="text-ink-hint text-caption">
          {documentosElegidos.length} de {documentos.length}
        </p>
      </div>

      {documentos.length === 0 ? (
        <p className="text-ink-muted text-caption">
          Este informe todavía no declara antecedentes legales. Márcalos en su pestaña de
          antecedentes legales y aparecerán acá.
        </p>
      ) : (
        <div className="divide-border bg-card divide-y rounded-xl border">
          {documentos.map((documento) => {
            const id = `documento-${documento.id}`;

            return (
              <label
                key={documento.id}
                htmlFor={id}
                className="hover:bg-muted/50 flex cursor-pointer items-start gap-3 px-4 py-3"
              >
                <Checkbox
                  id={id}
                  checked={documentosElegidos.includes(documento.id)}
                  onCheckedChange={() => alternar(documento.id)}
                  className="mt-0.5"
                />
                <p className="text-ink-body text-body">{documento.nombre}</p>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
